import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ModulesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ThemeCountCell,
} from "./ModulesPage.styles";
import Sidebar from "../../../components/Sidebar";
import Table from "../../../components/Table";
import IconButton from "../../../components/Buttons/IconButton";
import { Toast } from "primereact/toast";
import delIcon from "../../../assets/icons/delIcon.svg";
import editIcon from "../../../assets/icons/editIcon.svg";

import {
  deleteModule,
  fetchModules,
  createModule,
  updateModule,
  fetchModuleSchemas,
} from "../../../services/documentService/documentService";

import { Module, ModuleSchema } from "../../../services/documentService/documentService.types";
import Button from "../../../components/Buttons/Button";
import { ButtonWrapper } from "../TemplatesPage.styles";
import { ConfirmModal } from "../../../components/Modals";
import DeletedRow from "../../../components/DeletedRow";
import RestoredRow from "../../../components/RestoredRow";
import BreadcrumbLink from "../../../components/BreadcrumbLink/BreadcrumbLink";

import ModuleModal from "../../../components/Modals/ModuleModal/ModuleModal";
import LessonsButton from "../../../components/Buttons/LessonsButton";
import { ButtonsContainer } from "../../DocumentVersionsPage/DocumentVersionsPage.styles";

const ModulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(7);
  const [orderBy, setOrderBy] = useState<string>("");
  const toast = useRef<Toast>(null);

  const [searchParams] = useSearchParams();

  const documentName = searchParams.get("documentName") || "Неизвестный документ";
  const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
  const { documentId } = useParams<{ documentId: string }>();
  const templateId = searchParams.get("templateId") || "0";

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);

  // Состояния для уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Состояния для работы со схемами
  const [isModuleSchemaOpen, setIsModuleSchemaOpen] = useState<boolean>(false);
  const [moduleSchemas, setModuleSchemas] = useState<ModuleSchema[]>([]);
  const [editingModule, setEditingModule] = useState<Module | null>(null); // null - значит создаём, иначе редактируем

  useEffect(() => {
    if (!templateId) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать тариф и пользователя.",
      });
      navigate("/login");
      return;
    }
    loadModules();
  }, [templateId, currentPage, pageSize, orderBy]);

  const loadModules = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const response = await fetchModules(documentId || "", currentPage, pageSize, orderBy);
      setModules(response.results);
      setTotalPages(Math.ceil(response.count / pageSize));
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Ошибка загрузки модулей",
        life: 3000,
      });
      console.error("Ошибка при загрузке модулей:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy("");
    } else {
      setOrderBy(field);
    }
  };

  // ---------- Удаление ----------
  const openDeleteModal = (mod: Module) => {
    setModuleToDelete(mod);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setModuleToDelete(null);
  };

  const confirmDelete = () => {
    if (moduleToDelete) {
      handleDelete(moduleToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteModule(id);
        setPendingDelete(null);
        toast.current?.show({
          severity: "success",
          summary: "Удалено",
          detail: "Модуль успешно удален",
          life: 2000,
        });
        loadModules();
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить модуль",
          life: 3000,
        });
        console.error("Ошибка при удалении модуля:", error);
        setPendingDelete(null);
      }
    }, 3000);
  };

  const restoreModuleHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.current?.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление модуля отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  // ---------- Создание модуля ----------
  const openModuleSchemaModalForCreate = async () => {
    try {
      if (!templateId) return;
      // Загружаем схему
      const resp = await fetchModuleSchemas(Number(templateId));
      setModuleSchemas(resp ?? []);
      setEditingModule(null);
      setIsModuleSchemaOpen(true);
    } catch (err) {
      console.error("Ошибка при загрузке ModuleSchema:", err);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить схему модуля",
        life: 3000,
      });
    }
  };

  const handleModuleSchemaSubmitCreate = async (formData: Record<string, string>) => {
    try {

      const moduleName = formData["title"] || "Без названия";
      const ppNumber = formData["pp_number"] || "0";

      const createdModule = await createModule(Number(documentId), {
        name: moduleName,
        pp_number: ppNumber,
        version: documentId,
        data: formData,
      });

      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: `Модуль "${moduleName}" создан`,
        life: 3000,
      });

      setRecentlyCreated(createdModule.id);
      setIsModuleSchemaOpen(false);
      loadModules();
      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (error) {
      console.error("Ошибка при создании модуля:", error);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать модуль",
        life: 3000,
      });
    }
  };

  // ---------- Редактирование модуля ----------
  const openModuleSchemaModalForEdit = async (mod: Module) => {
    try {
      if (!templateId) return;
      const resp = await fetchModuleSchemas(Number(templateId));
      setModuleSchemas(resp ?? []);
      setEditingModule(mod);
      setIsModuleSchemaOpen(true);
    } catch (err) {
      console.error("Ошибка при загрузке схемы для редактирования модуля:", err);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить схему модуля",
        life: 3000,
      });
    }
  };

  const handleModuleSchemaSubmitEdit = async (formData: Record<string, string>) => {
    if (!editingModule) return;
    try {
      const moduleName = formData["title"] || editingModule.name || "Без названия";
      const ppNumber = formData["pp_number"] || editingModule.pp_number || "0";

      await updateModule(editingModule.id, {
        name: moduleName,
        pp_number: ppNumber,
        version: documentId,
        data: formData,
      });

      toast.current?.show({
        severity: "success",
        summary: "Успех",
        detail: `Модуль "${moduleName}" обновлён`,
        life: 3000,
      });

      setRecentlyEdited(editingModule.id);
      setIsModuleSchemaOpen(false);
      loadModules();
      setTimeout(() => setRecentlyEdited(null), 3000);
    } catch (error) {
      console.error("Ошибка при обновлении модуля:", error);
      toast.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось обновить модуль",
        life: 3000,
      });
    }
  };

  // Формирование заголовков таблицы
  let headers = [
    { label: "Название модуля", key: "name", fieldName: "name" },
    { label: "Порядковый номер модуля", key: "pp_number", fieldName: "pp_number" },
    { label: "Количество тем", key: "theme_count", sortable: false, fieldName: "theme_count" },
  ];
  // Проверяем, нужен ли столбец для расписания
  if (modules.length > 0 && modules[0].with_timetable) {
    headers.push({ label: "Недели", key: "timetable_view", sortable: false, fieldName: "timetable_view" });
  }
  headers = headers.concat([
    { label: "Темы", key: "theme_see", sortable: false, fieldName: "theme_see" },
    { label: "", key: "buttons", sortable: false, fieldName: "edit" },
  ]);

  const rows = modules.map((mod) => {
    if (mod.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={mod.id}
            onRestore={restoreModuleHandler}
            colSpan={headers.length}
            message={`Модуль "${mod.name}" будет удален через 3 секунды.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    const row: any = {
      name: mod.name,
      pp_number: mod.pp_number,
      theme_count: <ThemeCountCell>{mod.theme_count}</ThemeCountCell>,
      theme_see: <LessonsButton
        text={mod.theme_count === 0 ? "Добавить темы" : "Смотреть"}
        href={`/modules/${documentId}/${mod.id}/themes`}
        queryParams={{
          templateId: `${templateId}`,
          documentName: `${mod.version_name}`,
          documentId: `${documentId}`,
          moduleName: `${mod.name}`,
          templateName: `${templateName}`,

        }}
      />,
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openModuleSchemaModalForEdit(mod)}
          />
          <IconButton
            icon={delIcon}
            altText="Удалить"
            onClick={() => openDeleteModal(mod)}
          />
        </ButtonsContainer>
      ),
    };

    // Если модуль поддерживает расписание
    if (modules.length > 0 && modules[0].with_timetable) {
      row.timetable_count = mod.timetable_count;
      row.timetable_view = (
        <LessonsButton
          text={mod.timetable_count === 0 ? "Добавить расписание" : "Смотреть"}
          href={`/modules/${documentId}/${mod.id}/timetables`}
          queryParams={{
            templateId: `${templateId}`,
            documentName: `${mod.version_name}`,
            moduleName: `${mod.name}`,
            documentId: `${documentId}`,
            templateName: `${templateName}`,
          }}
        />
      );
    }
    return row;
  });

  if (recentlyRestored !== null) {
    const restoredIndex = modules.findIndex((m) => m.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Модуль успешно восстановлен" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = modules.findIndex((m) => m.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        name: <RestoredRow colSpan={headers.length} message="Модуль успешно создан" />,
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = modules.findIndex((m) => m.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        name: <RestoredRow colSpan={headers.length} message="Модуль успешно отредактирован" />,
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  /**
   * Генерируем initialValues для ModuleModal из data поля, если редактируем
   */
  const getInitialValues = (): Record<string, string> => {
    if (!editingModule || !editingModule.data) return {};
    // Возвращаем объект (ключ => значение)
    // При желании добавляем fallback для name, title, и пр.
    return editingModule.data;
  };

  /**
   * Обработка сабмита из ModuleModal: 
   * если editingModule = null -> создание, 
   * иначе -> редактирование
   */
  const handleModuleModalSubmit = (formData: Record<string, string>) => {
    if (!editingModule) {
      handleModuleSchemaSubmitCreate(formData);
    } else {
      handleModuleSchemaSubmitEdit(formData);
    }
  };

  return (
    <ModulesPageContainer>
      <Toast ref={toast} />
      <Sidebar />
      <ContentWrapper>
        {/* Хлебные крошки */}
        <div style={{ marginBottom: "16px" }}>
          <BreadcrumbLink href="/documents" text="Шаблоны документов" />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/documents/${templateId}/versions?templateName=${templateName}`}
            text={`Шаблон ${templateName}`}
          />
          <span style={{ margin: "0 8px" }}>/</span>
          <BreadcrumbLink
            href={`/versions/${documentId}/modules?documentName=${documentName}&templateId=${templateId}&templateName=${templateName}`}
            text={`Модули документа ${documentName}`}
          />
        </div>
        <HeaderContainer>
          <Title>Модули</Title>
          <ButtonWrapper>
            <Button onClick={openModuleSchemaModalForCreate} variant="active">
              Добавить модуль
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="name"
        />
      </ContentWrapper>

      {/* Окно подтверждения удаления */}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить модуль "${moduleToDelete?.name}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}

      {/* Модальное окно для создания/редактирования модуля через схему */}
      {isModuleSchemaOpen && (
        <ModuleModal
          title={editingModule ? `Редактирование модуля "${editingModule.name}"` : "Новый модуль"}
          fields={moduleSchemas ?? []}
          onSubmit={handleModuleModalSubmit}
          onCancel={() => setIsModuleSchemaOpen(false)}
          submitButtonText="Сохранить"
          initialValues={getInitialValues()}
        />
      )}
    </ModulesPageContainer>
  );
};

export default ModulesPage;
